import React from 'react';
import PropTypes from 'prop-types';

import {Box, CircularProgress, Divider, Paper, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import ClientChats from 'views/scenes/caregiver/detail/caregiver_scenes/caregiver_feed/caregiver_chat/ClientChats'; // referencing directly from /caregiver

import backend from 'utils/backend';

const CaregiverChat = (props) => {
  const {caregiver, clientCareplan, clientData, clientRn} = props;
  const {caregiverRn} = caregiver;
  const [caregiverName, setCaregiverName] = React.useState(null);
  const [conversations, setConversations] = React.useState([]);
  const [conversationsLoading, setConversationsLoading] = React.useState(false);
  const [messages, setMessages] = React.useState({});
  const [messagesLoading, setMessagesLoading] = React.useState({});

  React.useEffect(() => {
    let active = true;

    backend.caregiver.getCaregiver(caregiverRn)
        .then((output) => {
          if (active) {
            setCaregiverName(output?.caregiverDetail?.caregiverName || null);
          }
        })
        .catch(() => {
          if (active) {
            setCaregiverName(null);
          }
        });

    return () => {
      active = false;
    };
  }, [caregiverRn]);

  const caregiverBackend = React.useMemo(() => ({
    chat: {
      loadConversations: async () => {
        setConversationsLoading(true);

        try {
          const output = await backend.communication.getConversationList(caregiverRn);
          setConversations(output?.list || []);
        } catch (error) {
          console.error(error);
          setConversations([]);
        } finally {
          setConversationsLoading(false);
        }
      },
      loadConversationMessages: async (conversationRn) => {
        if (!conversationRn) {
          return;
        }

        setMessagesLoading((current) => ({
          ...current,
          [conversationRn]: true,
        }));

        try {
          let output = [];
          let lastEvaluatedKey = null;

          do {
            const result = await backend.communication.getConversationMessages(
                caregiverRn,
                conversationRn,
                lastEvaluatedKey,
            );
            output = [...output, ...(result?.list || [])];
            lastEvaluatedKey = result?.lastEvaluatedKey || null;
          } while (lastEvaluatedKey);

          setMessages((current) => ({
            ...current,
            [conversationRn]: output,
          }));
        } catch (error) {
          console.error(error);
          setMessages((current) => ({
            ...current,
            [conversationRn]: [],
          }));
        } finally {
          setMessagesLoading((current) => ({
            ...current,
            [conversationRn]: false,
          }));
        }
      },
      sendConversationMessage: async ({conversationRn, requesterRn, message, files}) => {
        if (!conversationRn) {
          return;
        }

        await backend.communication.createChannelMessage(caregiverRn, conversationRn, {
          requesterRn,
          message,
          source: 'WEB',
          files,
        });
      },
    },
  }), [caregiverRn]);

  const caregiverData = React.useMemo(() => ({
    clients: {
      [clientRn]: {
        clientRn,
        clientDetail: clientData,
        careplan: clientCareplan,
      },
    },
  }), [clientCareplan, clientData, clientRn]);

  return (
    <Paper
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 'none',
      }}
    >
      <Box sx={{px: 2, py: 1.5}}>
        <Typography sx={{fontWeight: 600}}>
          {caregiverName || translate('client.ClientDetail.Chat.UnknownCaregiver')}
        </Typography>
      </Box>
      <Divider />
      <ClientChats
        caregiverRn={caregiverRn}
        caregiverData={caregiverData}
        caregiverBackend={caregiverBackend}
        caregiverChatConversations={conversations}
        caregiverChatConversationsLoading={conversationsLoading}
        caregiverChatMessages={messages}
        caregiverChatMessagesLoading={messagesLoading}
        filterClientRn={clientRn}
        includeSupportChats={false}
      />
    </Paper>
  );
};

CaregiverChat.propTypes = {
  caregiver: PropTypes.object.isRequired,
  clientCareplan: PropTypes.object.isRequired,
  clientData: PropTypes.object.isRequired,
  clientRn: PropTypes.string.isRequired,
};

const CaregiverChats = (props) => {
  const {clientCareplan, clientData, clientRn} = props;
  const caregivers = Object.values(clientData?.caregivers || {});

  if (!clientData) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{minHeight: 300}}>
        <CircularProgress />
      </Stack>
    );
  }

  if (caregivers.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{minHeight: 300}}>
        <Typography color="text.secondary">
          <Translate expression='client.ClientDetail.Chat.NoCaregivers' />
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{p: 2}}>
      {
        caregivers.map((caregiver) => (
          <CaregiverChat
            key={caregiver.caregiverRn}
            caregiver={caregiver}
            clientCareplan={clientCareplan}
            clientData={clientData}
            clientRn={clientRn}
          />
        ))
      }
    </Stack>
  );
};

CaregiverChats.propTypes = {
  clientCareplan: PropTypes.object.isRequired,
  clientData: PropTypes.object.isRequired,
  clientRn: PropTypes.string.isRequired,
};

export default React.memo(CaregiverChats);
